import React from 'react';
import { styled } from '@mui/system';
import { Box } from '@mui/material';
import { CHAIN_TOKENS, SUPPORTED_NETWORKS } from 'constants/index';
import {
  EthBlueIcon,
  EthRedIcon,
  NRGLogoDarkIcon,
  ShibariumBlueIcon,
  ShibariumRedIcon,
  ApeChainBlueIcon,
  ApeChainRedIcon,
  BerachainBlueIcon,
  BerachainRedIcon,
  TenBlueIcon,
  TenRedIcon,
  MonadBlueIcon,
  MonadRedIcon,
  GMITokenLogo,
} from 'components/ImageComponent';
import Dropdown from './Dropdown';
import { formatNumber } from 'utils/numbers';

/********************  Styled Components  ********************/
const BalanceBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  border: `1px solid ${theme.palette.border.lighter}`,
  borderRadius: 10,
  overflow: 'hidden',
  marginBottom: theme.spacing(1),
}));

const StyledText = styled('p')(({ theme }) => ({
  color: theme.palette.icon.tertiary,
  fontSize: 14,
  lineHeight: '20px',
  letterSpacing: '0.25px',
  fontWeight: 500,
  margin: 0,
}));

const LabelBox = styled(Box)(() => ({
  display: 'flex',
  fontSize: 16,
  fontWeight: 600,
  alignItems: 'center',
}));

/********************  Main Component  ********************/
const ContractBox = ({ balances, visibility, setWalletSidebarOpen, chainId }) => {
  // Get valid tokens for the current chain - much simpler now!
  const getValidTokensForChain = (chainId) => {
    return CHAIN_TOKENS[chainId] || ['ETH', 'WETH']; // Default to ETH/WETH for unknown chains
  };
  const getTokenIcon = (token) => {
    const symbol = token.symbol;
    const isWrapped = token.isWrapped || symbol.startsWith('W');

    // Check chain-specific tokens based on current chainId

    // Energi tokens (only show on Energi chains)
    if (chainId === SUPPORTED_NETWORKS.Energi && (symbol === 'NRG' || symbol === 'WNRG')) {
      return <NRGLogoDarkIcon width={32} height={32} style={{ marginRight: 16 }} />;
    }

    // Shibarium tokens (only show on Shibarium chains)
    if (chainId === SUPPORTED_NETWORKS.Shibarium && (symbol === 'BONE' || symbol === 'WBONE')) {
      return isWrapped ? (
        <ShibariumRedIcon width={32} height={32} style={{ marginRight: 16 }} />
      ) : (
        <ShibariumBlueIcon width={32} height={32} style={{ marginRight: 16 }} />
      );
    }

    // ApeChain tokens (only show on ApeChain)
    if (chainId === SUPPORTED_NETWORKS.ApeChain && (symbol === 'APE' || symbol === 'WAPE')) {
      return isWrapped ? (
        <ApeChainRedIcon width={32} height={32} style={{ marginRight: 16 }} />
      ) : (
        <ApeChainBlueIcon width={32} height={32} style={{ marginRight: 16 }} />
      );
    }

    // BeraChain tokens (only show on BeraChain)
    if (chainId === SUPPORTED_NETWORKS.Berachain && (symbol === 'BERA' || symbol === 'WBERA')) {
      return isWrapped ? (
        <BerachainRedIcon width={32} height={32} style={{ marginRight: 16 }} />
      ) : (
        <BerachainBlueIcon width={32} height={32} style={{ marginRight: 16 }} />
      );
    }

    // TEN tokens (only show on TEN chain)
    if (chainId === SUPPORTED_NETWORKS.Ten && (symbol === 'ETH' || symbol === 'WETH')) {
      return isWrapped ? (
        <TenRedIcon
          src={require('assets/images/tenChain.jpg')}
          alt="TEN"
          width={32}
          height={32}
          style={{ marginRight: 16 }}
        />
      ) : (
        <TenBlueIcon
          src={require('assets/images/tenChain.jpg')}
          alt="TEN"
          width={32}
          height={32}
          style={{ marginRight: 16 }}
        />
      );
    }

    // Monad tokens (only show on Monad chain)
    if (chainId === SUPPORTED_NETWORKS.Monad && (symbol === 'MON' || symbol === 'WMON')) {
      return isWrapped ? (
        <MonadRedIcon width={32} height={32} style={{ marginRight: 16 }} />
      ) : (
        <MonadBlueIcon width={32} height={32} style={{ marginRight: 16 }} />
      );
    }

    // GMI token (special case - can appear on multiple chains but with GMI branding)
    if (symbol === 'GMICV' || symbol === 'GMI') {
      return <GMITokenLogo width={32} height={32} style={{ marginRight: 16 }} />;
    }

    // Default ETH icons for ETH/WETH on all other chains
    if (symbol === 'WETH') {
      return <EthRedIcon width={32} height={32} style={{ marginRight: 16 }} />;
    }

    if (symbol === 'ETH') {
      return <EthBlueIcon width={32} height={32} style={{ marginRight: 16 }} />;
    }

    // Default fallback
    return <EthBlueIcon width={32} height={32} style={{ marginRight: 16 }} />;
  };

  const dropdownList = {
    ETH: [
      { text: 'Add ETH', path: '/#', key: 'ETH_add' },
      { text: 'Wrap ETH', path: '/#', key: 'ETH_wrap' },
    ],
    WETH: [
      { text: 'Add ETH', path: '/#', key: 'WETH_add' },
      { text: 'Unwrap ETH', path: '/#', key: 'WETH_wrap' },
    ],
    GMI: [
      { text: 'Add GMI', path: '/#', key: 'GMI_add' },
      { text: 'Buy GMI', path: '/#', key: 'GMI_wrap' },
    ],
    NRG: [
      { text: 'Add NRG', path: '/#', key: 'NRG_add' },
      {
        text: 'Buy NRG',
        path: 'https://energi.world/get-nrg/',
        key: 'NRG_wrap',
      },
    ],
    WNRG: [
      { text: 'Add WNRG', path: '/#', key: 'WNRG_add' },
      { text: 'Buy WNRG', path: '/#', key: 'WNRG_wrap' },
    ],
    BONE: [
      { text: 'Add BONE', path: '/#', key: 'BONE_add' },
      { text: 'Wrap BONE', path: '/#', key: 'BONE_wrap' },
    ],
    WBONE: [
      { text: 'Add BONE', path: '/#', key: 'WBONE_add' },
      { text: 'Unwrap BONE', path: '/#', key: 'WBONE_wrap' },
    ],
    APE: [
      { text: 'Add APE', path: '/#', key: 'APE_add' },
      { text: 'Wrap APE', path: '/#', key: 'APE_wrap' },
    ],
    WAPE: [
      { text: 'Add APE', path: '/#', key: 'WAPE_add' },
      { text: 'Unwrap APE', path: '/#', key: 'WAPE_wrap' },
    ],
    BERA: [
      { text: 'Add BERA', path: '/#', key: 'BERA_add' },
      { text: 'Wrap BERA', path: '/#', key: 'BERA_wrap' },
    ],
    WBERA: [
      { text: 'Add BERA', path: '/#', key: 'WBERA_add' },
      { text: 'Unwrap BERA', path: '/#', key: 'WBERA_wrap' },
    ],
    MON: [
      { text: 'Add MON', path: '/#', key: 'MON_add' },
      { text: 'Wrap MON', path: '/#', key: 'MON_wrap' },
    ],
    WMON: [
      { text: 'Add MON', path: '/#', key: 'WMON_add' },
      { text: 'Unwrap MON', path: '/#', key: 'WMON_wrap' },
    ],
  };

  if (!balances?.length) {
    return null;
  }

  const validTokens = getValidTokensForChain(chainId);

  return (
    <div>
      {balances.map((token, index) =>
        validTokens.includes(token.symbol) ? (
          <BalanceBox key={index}>
            <LabelBox>
              {getTokenIcon(token)}
              <Box>
                {token.symbol === 'GMICV' ? 'GMI' : token.symbol}
                <StyledText>{token.symbol === 'GMICV' ? 'GonnaMakeIt' : token.name}</StyledText>
              </Box>
            </LabelBox>
            <LabelBox>
              <Box style={{ marginRight: 8, textAlign: 'end' }}>
                {visibility ? token.balance : '*****'}
                <StyledText>{visibility ? `$ ${formatNumber(token.value)}` : '*****'}</StyledText>
              </Box>
              <Dropdown
                symbol={token.symbol}
                list={dropdownList[token.symbol]}
                setWalletSidebarOpen={setWalletSidebarOpen}
              />
            </LabelBox>
          </BalanceBox>
        ) : (
          ''
        ),
      )}
    </div>
  );
};

export default ContractBox;
